import styled from 'styled-components';
import { CameraHeader } from './components/header';
import { CameraComponent } from './components/imageCamera';
import { CameraNotes } from './components/notes';
import { SaveVideoButton, TakeNextButton } from './components/takeNext';
import { OpenQuestionsButton } from './components/questionsButton';
import { DamagedListController, ImageListController } from './components/imageList';
import { HelperText } from './components/helperText';
import { useCameraViewContext } from './context';
import { DamageCheckbox } from './components/damage_checkbox';
import { Counter } from './components/counter';
import { TakePhoto } from './components/takePhoto';
import { ImagePreview } from './components/imagePreview';
import { VerticalControls } from './components/controls';
import { InFormQuestionsDialog } from '../InFormQuestionsDialog';
import { ConfirmDialog } from '../ConfirmDialog';
import { HelperTextVerticalToggler } from './components/helperText/helperTextVerticalToggler';
import { ImageNotesDialog } from '../ImageNotesDialog';
import { Level2Selection } from './components/level2Selection';
import { CameraTiresGrade } from './components/cameraTiresGrade';
import { useField } from 'react-final-form';
import { TakeVideo } from './components/takeVideo';
import { VideoPreview } from './components/VideoPreview';
import { CameraPlateDatePicker } from './components/cameraPlateDates';
import { ExteriorCategorySelection, ImageCategoryCheckbox } from './components/imageCategorySelection';
import { EXTERIOR_CATEGORIES } from './constants/imageCategory';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: #000;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  padding-bottom: 66px;
`;

const TextWrapper = styled.div`
  padding: 6px 10px 10px 16px;
`;

const TakePictureWrapper = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Space = styled.div`
  height: 12px;
`;

const NotesRow = styled.div`
  display: flex;
  gap: 8px;
  margin: 8px 16px 0px 16px;
`;

export const VerticalView = () => {
  return (
    <Wrapper>
      <UpperPart />
      <LowerPart />
    </Wrapper>
  );
};

const UpperPart = () => {
  const { level1SectionId, isDamageSection } = useCameraViewContext();
  const showLevel2Selection = level1SectionId?.startsWith('exterior');

  return (
    <>
      <ImageNotesDialog />
      <CameraHeader />
      {showLevel2Selection && <Level2Selection />}
      {isDamageSection ? <DamagedListController /> : <ImageListController />}
    </>
  );
};

const LowerPart = () => {
  const { state, isDamageSection, isVideoMode, type, level1SectionId } = useCameraViewContext();

  const showHelperText = !isVideoMode && type !== 'initial_image';

  const controlsLift = level1SectionId?.startsWith('exterior') ? 36 : 0;

  if (isDamageSection) {
    return null;
  }

  return (
    <>
      <>
        <CameraComponent>
          {!isVideoMode && <CheckboxControls />}
          {showHelperText && <HelperTextVerticalToggler lift={controlsLift} />}
          {!isVideoMode && <Counter lift={controlsLift} />}
        </CameraComponent>

        {state === 'taking_photo' ? <TakePhotoView /> : <ConfirmingPhotoView />}
        <InFormQuestionsDialog />
        <ConfirmDialog />
      </>
    </>
  );
};

const TakePhotoView = () => {
  const { level1SectionId, imageIndex, isVideoMode, type } = useCameraViewContext();
  const { input } = useField(`${level1SectionId}.images.${imageIndex}`);
  const showTiresGrade = input.value?.type?.endsWith('_tire');
  const showPlatePicker = input.value?.type === 'tires.license_plate_registration';

  const showMessage = type !== 'initial_image';

  return (
    <>
      {!isVideoMode && (
        <NotesRow>
          {showMessage && <CameraNotes />}
          {showTiresGrade && <CameraTiresGrade />}
          {showPlatePicker && <CameraPlateDatePicker />}
        </NotesRow>
      )}

      <TextWrapper>
        <HelperText />
      </TextWrapper>
      <TakePictureWrapper>{isVideoMode ? <TakeVideo /> : <TakePhoto />}</TakePictureWrapper>
    </>
  );
};

const ConfirmingPhotoView = () => {
  const { level1SectionId, imageIndex, isVideoMode, type } = useCameraViewContext();
  const { input } = useField(`${level1SectionId}.images.${imageIndex}`);
  const showTiresGrade = input.value?.type?.endsWith('_tire');
  const showPlateDates = input.value?.type === 'tires.license_plate_registration';
  const showMessage = type !== 'initial_image';
  const controlsLift = level1SectionId?.startsWith('exterior') ? 36 : 0;

  const showQuestionsButton = type !== 'initial_image';

  if (isVideoMode) {
    return (
      <>
        <VideoPreview>
          <VerticalControls />
        </VideoPreview>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <OpenQuestionsButton />
          <SaveVideoButton />
        </div>
      </>
    );
  }
  return (
    <>
      <ImagePreview>
        <VerticalControls />
        <CheckboxControls />
        <Counter lift={controlsLift} />
      </ImagePreview>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <NotesRow>
          {showMessage && <CameraNotes />}
          {showTiresGrade && <CameraTiresGrade />}
          {showPlateDates && <CameraPlateDatePicker />}
        </NotesRow>
        {showQuestionsButton && <OpenQuestionsButton />}
        <TakeNextButton />
      </div>
    </>
  );
};

const CheckboxControlsWrapper = styled.div`
  position: absolute;
  bottom: 10px;
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

const CheckboxControls = () => {
  return (
    <CheckboxControlsWrapper>
      <DamageCheckbox withBackground />
    </CheckboxControlsWrapper>
  );
};
